import { verifyEmailRequestSchema, type VerifyEmailRequest } from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { ResendCodeButton } from '@/components/resend-code-button';
import { useApiClient } from '@/hooks/use-api-client';
import {
  centralLoginPath,
  signupHandoffPhone,
  VERIFY_EMAIL_PARAM,
  verifyPhonePath,
} from '@/lib/auth-paths';

/** Self-serve signup only ever creates a console tenant, so its sign-in is the admin client. */
const ADMIN_CLIENT = 'admin' as const;

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

/**
 * A pasted address routinely carries whitespace, and a pasted code carries a
 * trailing space more often than not — normalise in front of the schema so the
 * SAME value is validated and sent.
 */
const normalizeValues = (values: VerifyEmailRequest): VerifyEmailRequest => ({
  email: values.email.trim(),
  code: values.code.trim(),
});

/**
 * Step two of self-serve signup: redeem the emailed code (S26 Phase 3).
 *
 * Public, like `/signup` — the account is `PENDING` and cannot be signed in to,
 * so a guard here would lock out the only person who needs the screen.
 *
 * `email` is prefilled from the `?email=` the signup screen appends and stays
 * editable: a convenience, never a capability, because a code only works against
 * the address it was issued for.
 *
 * ONE ERROR CODE COVERS EVERY CALLER-VISIBLE FAILURE HERE, AND THE COPY IS
 * WRITTEN FOR THAT. `POST /api/web/auth/verify-email` answers
 * `VERIFICATION_CODE_INVALID` for a wrong code, an expired one, one already
 * spent, one that ran out of attempts, an address with no outstanding code, and
 * an account that no longer qualifies — deliberately, so a caller cannot narrow a
 * guess. The screen therefore says what is true of all of them ("that code isn't
 * valid; it may have expired or already been used") and invents no distinction
 * the API does not make.
 *
 * Its states: idle, submitting (`formState.isSubmitting`, so a double-click
 * cannot burn the code twice), error — one translated message for the code the
 * API returned — and done, which is either a navigation to `/verify-phone` when a
 * channel remains or the "your account is ready" card when the tenant is live.
 */
export function VerifyEmailPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isActivated, setIsActivated] = useState(false);

  /**
   * The address the previous screen named, read once for the description. The
   * FIELD is what gets submitted; this is only the copy, so it does not jitter
   * while somebody is correcting a typo.
   */
  const addressedTo = searchParams.get(VERIFY_EMAIL_PARAM) ?? '';
  /** Carried in location state by `/signup`; empty when the screen was reached directly. */
  const handoffPhone = signupHandoffPhone(location.state);

  /** One message for any invalid field — the only translated copy we have. */
  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');

  const resolver = useMemo<Resolver<VerifyEmailRequest>>(() => {
    const validate = zodResolver(verifyEmailRequestSchema, {
      path: [],
      async: true,
      errorMap: () => ({ message: validationMessage }),
    });
    return (values, context, options) => validate(normalizeValues(values), context, options);
  }, [validationMessage]);

  const form = useForm<VerifyEmailRequest>({
    resolver,
    defaultValues: { email: addressedTo, code: '' },
  });

  const onSubmit = form.handleSubmit(async (values: VerifyEmailRequest): Promise<void> => {
    setErrorCode(null);
    const request = normalizeValues(values);
    try {
      const result = await api.auth.verifyEmail(request);
      if (result.activated) {
        setIsActivated(true);
        return;
      }
      if (result.remainingChannels.includes('SMS')) {
        await navigate(verifyPhonePath(request.email, handoffPhone));
        return;
      }
      // Not activated and no channel named — nothing this screen can offer, and
      // claiming the account is ready would be a lie. Reported as unexpected.
      setErrorCode(UNEXPECTED_FAILURE);
    } catch (error: unknown) {
      setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        {isActivated ? (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('verifyDone.title')}
              </h1>
            </CardHeader>
            <CardContent>
              <Alert variant="success">
                <AlertDescription>{t('verifyDone.description')}</AlertDescription>
              </Alert>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full">
                <Link to={centralLoginPath(ADMIN_CLIENT)}>{t('verifyDone.signIn')}</Link>
              </Button>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader>
              <h1 className="text-3xl font-semibold text-primary text-balance">
                {t('verifyEmail.title')}
              </h1>
              <CardDescription>
                {addressedTo.length > 0
                  ? t('verifyEmail.description', { email: addressedTo })
                  : t('verifyEmail.descriptionNoAddress')}
              </CardDescription>
            </CardHeader>

            <Form {...form}>
              <form
                noValidate
                onSubmit={(event) => {
                  void onSubmit(event);
                }}
              >
                <CardContent className="flex flex-col gap-4">
                  {errorCode === null ? null : (
                    <p role="alert" className="text-danger text-sm text-pretty">
                      {errorMessage(t, errorCode)}
                    </p>
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('verifyEmail.emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            inputMode="email"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('verifyEmail.codeLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            autoCapitalize="none"
                            spellCheck={false}
                            width="sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>

                <CardFooter className="flex flex-col items-stretch gap-3">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                  >
                    {isSubmitting ? t('verifyEmail.submitting') : t('verifyEmail.submit')}
                  </Button>
                  {/* S26-TASK-021. Reads the LIVE field rather than the query
                      parameter, so somebody who is fixing a typo gets the new
                      code at the address they just corrected. */}
                  <ResendCodeButton channel="EMAIL" email={form.watch('email')} />
                  <Button asChild variant="link" className="w-full">
                    <Link to={centralLoginPath(ADMIN_CLIENT)}>{t('verifyDone.signIn')}</Link>
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </>
        )}
      </Card>
    </AuthCardLayout>
  );
}
